/**
 * complianceModel — plain-language reading of a change-tracker item for the Risk panel's Compliance reviews
 * (Bill 2026-11-04: "Everyone (Reid, Peter, Anthony, Jessica, examiners) must instantly understand each item: what it
 * is, why, what was done, what hasn't been done, why.").
 *
 * The briefing, vocabulary and history wording now live in @retail-ops/support-report (trackerBriefing.ts) so
 * ROP-teams' Pilot & requests reads the same words (Bill 2026-11-04); they are re-exported here unchanged. The
 * reviewing actions below are the Risk panel's own.
 */
import type { ReviewAction, ReviewRow } from '@/services/complianceReviewsApi';
import { personName } from '@retail-ops/support-report';

export {
  personName,
  statusWords,
  flagBadge,
  determinationWords,
  sourceWords,
  platformWords,
  categoryWords,
  linkWords,
  linkUrl,
  dayText,
  whenText,
  waitedText,
  briefing,
  notDone,
  historyLines,
} from '@retail-ops/support-report';
export type { Tone, FlagBadge, BriefLine, Briefing, HistoryLine } from '@retail-ops/support-report';

const lc = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

// ── reviewing ────────────────────────────────────────────────────────────────────────────────────────────────

export interface ActionDef {
  readonly action: ReviewAction;
  readonly label: string;
  readonly help: string;
  readonly placeholder: string;
  readonly tone: 'primary' | 'ok' | 'neutral' | 'danger';
}

export const REVIEW_ACTIONS: ReadonlyArray<ActionDef> = [
  {
    action: 'confirm',
    label: 'Confirm impact',
    tone: 'primary',
    help: 'This change matters to compliance. Your decision and note are recorded on the item.',
    placeholder: 'Which rule or process it affects, and anything the team should do',
  },
  {
    action: 'clear',
    label: 'Clear',
    tone: 'ok',
    help: 'This change does not affect compliance. Your decision and note are recorded on the item.',
    placeholder: 'Why it does not affect compliance',
  },
  {
    action: 'note',
    label: 'Add note',
    tone: 'neutral',
    help: 'Adds a note to the item’s history without changing anything else.',
    placeholder: 'Your note',
  },
  {
    action: 'request-revert',
    label: 'Request undo',
    tone: 'danger',
    help: 'Asks the developers to undo this change in the live app. They receive an Innovation Hub request.',
    placeholder: 'Why it should be undone',
  },
];

/** An undo can be asked for only once the change is live (released or checked). */
export function canRequestRevert(row: Pick<ReviewRow, 'status'>): boolean {
  const st = lc(row.status);
  return st === 'deployed' || st === 'verified';
}

export function actionsFor(row: Pick<ReviewRow, 'status'>): ReadonlyArray<ActionDef> {
  return REVIEW_ACTIONS.filter((a) => a.action !== 'request-revert' || canRequestRevert(row));
}

/** The Hub keeps up to 1,000 characters of a review note (TrackerRules.ReviewNoteMax). */
export const NOTE_MAX = 1000;

/** The reviewers' names joined for a sentence: "Jessica Beucler and Ginger McCullough". */
export function namesSentence(people: ReadonlyArray<{ upn: string; name: string }>): string {
  const names = people.map((p) => personName(p.upn, p.name));
  if (names.length <= 1) return names[0] ?? 'nobody yet';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
